import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { renderGiftCard } from '@/utils/inviteCard';
import { shareCardWithFriend, BOT_USERNAME } from '@/utils/shareCard';
import { ChevronLeft, Gift as GiftIcon, Copy, Check, Send, Heart, Clock } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { supabase } from '@/lib/supabase';

// «Подари заботу»: даритель платит полную цену, получатель получает срок бесплатно.
// ДНИ ДАРИТЕЛЮ НЕ НАЧИСЛЯЮТСЯ (решение Славы 10.08.2026) — награда деньгами/временем
// превращает поступок в сделку. Мотивация здесь: сам жест, статус «Круг заботы»,
// лестница доступа и весточка, когда друг дойдёт до финиша.
// Цена и срок задаются в RPC create_gift на сервере, клиент их не подставляет.

const SHOP_URL = 'https://kokorinpay.payform.ru/';

type GiftRow = {
  id: string;
  code: string;
  plan: 'month' | 'year';
  days: number;
  status: 'pending' | 'paid' | 'redeemed';
  note: string | null;
  redeemed_at: string | null;
  created_at: string;
};

export default function Gift() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [plan, setPlan] = useState<'month' | 'year'>('month');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [gifts, setGifts] = useState<GiftRow[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [sendingCode, setSendingCode] = useState<string | null>(null);

  const price = plan === 'year' ? 5990 : 1490;

  const loadGifts = useCallback(async () => {
    if (!profile?.id) return;
    const { data } = await supabase
      .from('gift_subscriptions')
      .select('id, code, plan, days, status, note, redeemed_at, created_at')
      .eq('giver_id', profile.id)
      .order('created_at', { ascending: false });
    setGifts((data ?? []) as GiftRow[]);
  }, [profile?.id]);

  useEffect(() => {
    loadGifts();
  }, [loadGifts]);

  // Вернулись с оплаты (urlSuccess) — вебхук мог ещё не долететь, поэтому
  // перечитываем список несколько раз с паузой.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('paid')) return;
    window.history.replaceState({}, '', window.location.pathname);
    const timers = [1000, 3000, 6000].map((ms) => setTimeout(loadGifts, ms));
    return () => timers.forEach(clearTimeout);
  }, [loadGifts]);

  const redeemedCount = gifts.filter((g) => g.status === 'redeemed').length;

  async function buy() {
    if (!profile?.id || loading) return;
    haptic('medium');
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('create_gift', {
        p_plan: plan,
        p_note: note.trim() || null,
      });
      if (error || !data?.success) {
        alert('Не получилось создать подарок. Попробуй ещё раз.');
        return;
      }

      // Ссылка на оплату — тот же магазин, что в Paywall, но помечаем платёж
      // как подарочный через customer_extra, чтобы вебхук не продлил подписку дарителю.
      const params = new URLSearchParams();
      params.set('do', 'pay');
      params.set('order_id', `GIFT${Date.now()}${Math.floor(Math.random() * 1000)}`);
      params.set('products[0][price]', String(data.amount));
      params.set('products[0][quantity]', '1');
      params.set(
        'products[0][name]',
        plan === 'year'
          ? 'Подарочная подписка Slava Health: питание, привычки, тренировки — 12 месяцев'
          : 'Подарочная подписка Slava Health: питание, привычки, тренировки — 30 дней',
      );
      // Фискализация (54-ФЗ), коды как в Paywall: услуга + полная оплата
      params.set('products[0][paymentObject]', '4');
      params.set('products[0][paymentMethod]', '4');
      params.set('customer_extra', `gift:${data.gift_id}`);
      params.set('callbackType', 'json');
      params.set('urlReturn', window.location.origin + '/gift');
      params.set('urlSuccess', window.location.origin + '/gift?paid=1');

      const url = `${SHOP_URL}?${params.toString()}`;
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.openLink) tg.openLink(url, { try_instant_view: false });
      else window.open(url, '_blank');

      setNote('');
      loadGifts();
    } finally {
      setTimeout(() => setLoading(false), 2000);
    }
  }

  function giftLink(code: string) {
    return `${window.location.origin}/?gift=${code}`;
  }

  function copy(code: string) {
    haptic('light');
    navigator.clipboard?.writeText(giftLink(code)).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1800);
    });
  }

  // Подарок уходит открыткой с Дофи (Слава 07.10.2026), а не голым текстом.
  // Кнопка под открыткой — сразу в бота с кодом подарка (GiftApplier ловит gift_КОД).
  async function share(g: GiftRow) {
    if (!profile?.id || sendingCode) return;
    haptic('medium');
    setSendingCode(g.code);
    try {
      const text =
        'Это тебе. Подписка на Slava Health — приложение с ИИ-коучем: питание, привычки, состояние. Всё уже оплачено, просто открой 🎁';
      const blob = await renderGiftCard({ name: profile.first_name, plan: g.plan });
      await shareCardWithFriend(blob, {
        userId: profile.id,
        key: 'gift',
        caption: text,
        link: `https://t.me/${BOT_USERNAME}?startapp=gift_${g.code}`,
        button: 'Открыть подарок 🎁',
        fileName: 'slava-health-gift.png',
      });
    } catch (e) {
      console.error('[gift share]', e);
    } finally {
      setSendingCode(null);
    }
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-bg pb-8">
      <div className="max-w-md mx-auto px-5 pt-6">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-1 text-ink-muted hover:text-ink mb-6 text-sm"
        >
          <ChevronLeft size={18} /> Назад
        </button>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-glow to-sky-glow items-center justify-center mb-4">
            <GiftIcon size={28} className="text-bg" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-3">Подари заботу</h1>
          <p className="text-ink-muted leading-relaxed text-[15px]">
            Есть человек, за которого ты переживаешь? Который уже полгода говорит
            «вот с понедельника точно»?
          </p>
          <p className="text-ink-muted leading-relaxed text-[15px] mt-2">
            Иногда ему нужен не совет. Нужно, чтобы кто-то просто взял и открыл дверь.
          </p>
        </motion.div>

        {/* Срок подарка */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="flex gap-2 p-1 bg-bg-elevated rounded-2xl mb-4"
        >
          <button
            onClick={() => { haptic('light'); setPlan('month'); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${
              plan === 'month' ? 'bg-bg text-ink shadow-sm' : 'text-ink-dim'
            }`}
          >
            30 дней
          </button>
          <button
            onClick={() => { haptic('light'); setPlan('year'); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
              plan === 'year' ? 'bg-bg text-ink shadow-sm' : 'text-ink-dim'
            }`}
          >
            Год
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-glow/15 text-emerald-glow text-[10px] font-bold">
              499 ₽/мес
            </span>
          </button>
        </motion.div>

        {/* Записка */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="card p-4 mb-4"
        >
          <div className="label mb-2">Напиши ему пару слов</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 300))}
            rows={3}
            placeholder="Не обязательно. Но именно это он перечитает"
            className="w-full bg-bg-elevated rounded-xl px-3 py-2.5 text-sm text-ink placeholder:text-ink-dim outline-none resize-none"
          />
          <div className="text-right text-[11px] text-ink-dim mt-1">{note.length}/300</div>
        </motion.div>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16 }}
          onClick={buy}
          disabled={loading}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-glow to-sky-glow text-bg font-display font-bold text-base shadow-lg active:scale-[0.98] transition-all disabled:opacity-60"
        >
          {loading ? 'Открываем оплату…' : `Подарить — ${price.toLocaleString('ru-RU')} ₽`}
        </motion.button>

        <p className="text-center text-[11px] text-ink-dim mt-3 leading-relaxed">
          После оплаты ты получишь ссылку — её и отправишь. Платить получателю ничего не нужно.
        </p>

        {/* Круг заботы */}
        {redeemedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-elevated border-emerald-glow/40 p-4 mt-6 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-glow/15 flex items-center justify-center shrink-0">
              <Heart size={18} className="text-emerald-glow" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm">Круг заботы</div>
              <div className="text-xs text-ink-dim">
                {redeemedCount}{' '}
                {redeemedCount === 1 ? 'подарок дошёл' : 'подарка(ов) дошло'} до человека
              </div>
            </div>
          </motion.div>
        )}

        {/* Мои подарки */}
        {gifts.length > 0 && (
          <div className="mt-6">
            <div className="label mb-3">Мои подарки</div>
            <div className="space-y-3">
              {gifts.map((g) => (
                <div key={g.id} className="card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm">
                      {g.plan === 'year' ? 'Год' : '30 дней'}
                    </div>
                    <GiftStatus status={g.status} />
                  </div>

                  {g.status === 'paid' && (
                    <>
                      <div className="text-xs text-ink-dim mb-3 leading-relaxed">
                        Подарок готов. Осталось самое человеческое — передать его лично.
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => share(g)}
                          disabled={sendingCode === g.code}
                          className="flex-1 py-2.5 rounded-xl bg-emerald-glow/15 text-emerald-glow text-sm font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
                        >
                          <Send size={15} /> Отправить
                        </button>
                        <button
                          onClick={() => copy(g.code)}
                          className="w-11 h-11 rounded-xl bg-bg-elevated text-ink-muted flex items-center justify-center active:scale-95 transition-transform"
                          aria-label="Копировать ссылку"
                        >
                          {copiedCode === g.code ? <Check size={17} /> : <Copy size={17} />}
                        </button>
                      </div>
                    </>
                  )}

                  {g.status === 'pending' && (
                    <div className="text-xs text-ink-dim flex items-center gap-1.5">
                      <Clock size={13} /> Ждём подтверждения оплаты
                    </div>
                  )}

                  {g.status === 'redeemed' && (
                    <div className="text-xs text-ink-dim">
                      Открыт{' '}
                      {g.redeemed_at
                        ? new Date(g.redeemed_at).toLocaleDateString('ru-RU')
                        : ''}
                      . Это началось с тебя.
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function GiftStatus({ status }: { status: GiftRow['status'] }) {
  const map: Record<GiftRow['status'], { text: string; cls: string }> = {
    pending: { text: 'Ждёт оплаты', cls: 'bg-bg-elevated text-ink-dim' },
    paid: { text: 'Готов к отправке', cls: 'bg-sky-glow/15 text-sky-glow' },
    redeemed: { text: 'Открыт', cls: 'bg-emerald-glow/15 text-emerald-glow' },
  };
  const s = map[status];
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.cls}`}>{s.text}</span>
  );
}
